import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { SubSink } from 'subsink';
import { ProjectService } from '../services/project.service';
import { OpenProjectService } from '../services/open-project.service';
import { ToastService } from '../shared/toast/toast.service';
import { IProjectSimpleModel } from '../shared/models/project-simple-model';

@Component({
  selector: 'pps-open-project-add-dialog',
  templateUrl: './open-project-add-dialog.component.html',
  styles: [
    `
    .open-project-add-dialog {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 420px;
      gap: 8px;
    }

    .dialog-grid-wrapper {
      flex: 1 1 auto;
      min-height: 320px;
    }

    .dialog-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      flex-wrap: wrap;
    }

    :host ::ng-deep .disabled-project-row .ag-cell {
      opacity: 0.55;
    }
    `
  ]
})
export class OpenProjectAddDialogComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  private gridApi: GridApi<IProjectSimpleModel> | null = null;
  private disabledProjectIdSet = new Set<number>();
  private hasAddedProjects = false;
  private onAdded: (() => void) | null = null;

  loading = false;
  localeText: { [key: string]: string } = {
    loadingOoo: 'Loading Project...',
  };

  adding = false;
  selectableSelectedCount = 0;
  projects: IProjectSimpleModel[] = [];

  columnDefs: ColDef<IProjectSimpleModel>[] = [
    {
      headerName: '',
      width: 50,
      resizable: false,
      sortable: false,
      filter: false,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      pinned: true
    },
    {
      headerName: 'Project No',
      field: 'projectNo',
      width: 140,
      floatingFilter: true,
      tooltipField: 'projectNo'
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 1,
      minWidth: 220,
      tooltipField: 'description'
    }
  ];

  gridOptions: GridOptions<IProjectSimpleModel> = {
    getRowId: (params: any) => `${params.data?.id ?? ''}`,
    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: true
    },
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    rowClassRules: {
      'disabled-project-row': (params: any) => this.disabledProjectIdSet.has(params?.data?.id)
    },
    onSelectionChanged: () => this.refreshSelectableSelectedCount(),
    onRowSelected: (params: any) => {
      // Keep already-added projects permanently selected so users can identify them.
      if (params?.data && this.disabledProjectIdSet.has(params.data.id) && !params.node.isSelected()) {
        params.node.setSelected(true, false);
      }
    },
    onCellClicked: (params: any) => {
      if (!params?.node || !params?.data) {
        return;
      }

      // Checkbox column keeps native checkbox behavior.
      if (params?.colDef?.checkboxSelection) {
        return;
      }

      if (this.disabledProjectIdSet.has(params.data.id)) {
        return;
      }

      params.node.setSelected(!params.node.isSelected(), false);
    }
  };

  constructor(
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig,
    private projectService: ProjectService,
    private openProjectService: OpenProjectService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const disabledProjectIds: number[] = this.dialogConfig?.data?.disabledProjectIds || [];
    disabledProjectIds.forEach(id => this.disabledProjectIdSet.add(id));
    this.onAdded = typeof this.dialogConfig?.data?.onAdded === 'function'
      ? this.dialogConfig.data.onAdded
      : null;
    this.loadSimpleProjects();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onGridReady(params: GridReadyEvent<IProjectSimpleModel>): void {
    this.gridApi = params.api;
    this.syncDisabledSelections();
  }

  closeDialog(): void {
    this.dialogRef.close(this.hasAddedProjects);
  }

  addSelectedProjects(): void {
    const selectedProjectIds = this.getSelectableSelectedProjectIds();
    if (!selectedProjectIds.length || this.adding) {
      return;
    }

    this.adding = true;
    this.subs.sink = this.openProjectService.add(selectedProjectIds)
      .subscribe(response => {
        if (response.succeed) {
          this.hasAddedProjects = true;
          selectedProjectIds.forEach(id => this.disabledProjectIdSet.add(id));
          this.gridApi?.deselectAll();
          this.syncDisabledSelections();
          this.gridApi?.refreshCells({ force: true });
          this.gridApi?.redrawRows();
          this.refreshSelectableSelectedCount();
          this.onAdded?.();
          this.toast.succeeded(`${selectedProjectIds.length} project(s) added to open projects.`);
        } else {
          this.toast.failed(`Failed to add project(s): ${response.errors.join('|')}`);
        }
      },
      () => {
        this.toast.failed('Failed to add selected project(s).');
      },
      () => {
        this.adding = false;
      });
  }

  private loadSimpleProjects(): void {
    this.loading = true;
    this.projects = [];

    this.subs.sink = this.projectService.getSimpleProjects()
      .subscribe(response => {
        if (response.succeed) {
          this.projects = response.result || [];
          this.syncDisabledSelections();
        } else {
          this.toast.failed(`Failed to load projects: ${response.errors.join('|')}`);
        }
      },
      () => {
        this.toast.failed('Failed to load projects.');
      },
      () => {
        this.loading = false;
      });
  }

  private getSelectableSelectedProjectIds(): number[] {
    if (!this.gridApi) {
      return [];
    }

    return this.gridApi.getSelectedNodes()
      .map(node => node?.data?.id)
      .filter((id): id is number => typeof id === 'number' && !this.disabledProjectIdSet.has(id));
  }

  private refreshSelectableSelectedCount(): void {
    this.selectableSelectedCount = this.getSelectableSelectedProjectIds().length;
  }

  private syncDisabledSelections(): void {
    if (!this.gridApi) {
      return;
    }

    this.gridApi.forEachNode(node => {
      const id = node?.data?.id;
      if (typeof id === 'number' && this.disabledProjectIdSet.has(id) && !node.isSelected()) {
        node.setSelected(true, false);
      }
    });
  }
}